import { useState, type SubmitEvent } from 'react'
import { CheckCircle2Icon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { useCreateLocationInquiryMutation } from '@/api/accommodation/hooks'
import { loadStoredInquiryForm, saveStoredInquiryForm } from './storage'

interface LocationInquiryFormProps {
  accommodation: AccommodationDetail
  onSent: () => void
  onClose: () => void
}

interface FormValues {
  name: string
  phone: string
  message: string
  gdpr: boolean
}

type FieldErrors = Partial<Record<keyof FormValues, string>>

const initialValues: FormValues = {
  name: '',
  phone: '',
  message: '',
  gdpr: false,
}

function validate(values: FormValues): FieldErrors {
  const errors: FieldErrors = {}

  if (values.name.trim().length < 2) {
    errors.name = 'Zadejte jméno (min. 2 znaky).'
  }
  if (!values.phone.trim()) {
    errors.phone = 'Zadejte telefon.'
  } else if (values.phone.replace(/\s/g, '').length < 9) {
    errors.phone = 'Telefon musí mít alespoň 9 číslic.'
  }
  if (values.message.trim().length < 10) {
    errors.message = 'Zpráva musí mít alespoň 10 znaků.'
  }
  if (!values.gdpr) {
    errors.gdpr = 'Pro odeslání je nutný souhlas se zpracováním údajů.'
  }

  return errors
}

export function LocationInquiryForm({ accommodation, onSent, onClose }: LocationInquiryFormProps) {
  const [values, setValues] = useState<FormValues>(() => ({
    ...initialValues,
    ...loadStoredInquiryForm(),
  }))
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSent, setIsSent] = useState(false)

  const mutation = useCreateLocationInquiryMutation(accommodation.slug)

  const updateField = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitError(null)
    const validation = validate(values)
    if (Object.keys(validation).length > 0) {
      setErrors(validation)
      return
    }

    mutation.mutate(
      {
        name: values.name.trim(),
        phone: values.phone.trim(),
        message: values.message.trim(),
      },
      {
        onSuccess: () => {
          saveStoredInquiryForm({
            name: values.name.trim(),
            phone: values.phone.trim(),
          })
          setIsSent(true)
          onSent()
        },
        onError: () => {
          setSubmitError('Zprávu se nepodařilo odeslat. Zkuste to prosím znovu, případně nás kontaktujte přímo.')
        },
      },
    )
  }

  if (isSent) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 py-10 text-center">
        <div className="space-y-1">
          <div className="flex justify-center rounded-full bg-green-50 p-1 text-green-600">
            <CheckCircle2Icon className="size-10" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Děkujeme za vaši zprávu.</h3>
          <p className="max-w-sm text-sm text-slate-600">
            Předáme ji ubytovateli a co nejdříve se vám ozveme na telefon{' '}
            <span className="font-medium text-slate-900">{values.phone.trim()}</span>.
          </p>
        </div>
        <Button type="button" variant="outline" onClick={onClose}>
          Zavřít
        </Button>
      </div>
    )
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="space-y-5">
      <FieldWrapper id="inquiry-name" label="Jméno" error={errors.name}>
        <Input
          id="inquiry-name"
          name="name"
          autoComplete="name"
          value={values.name}
          aria-invalid={!!errors.name}
          onChange={(e) => updateField('name', e.target.value)}
        />
      </FieldWrapper>

      <FieldWrapper id="inquiry-phone" label="Telefon" error={errors.phone}>
        <Input
          id="inquiry-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          value={values.phone}
          aria-invalid={!!errors.phone}
          onChange={(e) => updateField('phone', e.target.value)}
        />
      </FieldWrapper>

      <FieldWrapper id="inquiry-message" label="Zpráva" error={errors.message}>
        <Textarea
          id="inquiry-message"
          name="message"
          rows={5}
          placeholder={`Zeptejte se na cokoliv ohledně ubytování „${accommodation.name}“.`}
          value={values.message}
          aria-invalid={!!errors.message}
          onChange={(e) => updateField('message', e.target.value)}
        />
      </FieldWrapper>

      <div className="space-y-1">
        <label htmlFor="inquiry-gdpr" className="flex items-start gap-2 text-sm text-slate-700">
          <Checkbox
            id="inquiry-gdpr"
            checked={values.gdpr}
            aria-invalid={!!errors.gdpr}
            onCheckedChange={(checked) => updateField('gdpr', checked === true)}
            className="mt-0.5"
          />
          <span>Souhlasím se zpracováním osobních údajů pro účely zodpovězení dotazu.</span>
        </label>
        {errors.gdpr && <p className="pl-6 text-sm text-red-600">{errors.gdpr}</p>}
      </div>

      {submitError && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>}

      <Button type="submit" variant="red" className="w-full" disabled={mutation.isPending}>
        {mutation.isPending ? 'Odesílám…' : 'Odeslat dotaz'}
      </Button>
    </form>
  )
}

interface FieldWrapperProps {
  id: string
  label: string
  error?: string
  children: React.ReactNode
}

function FieldWrapper({ id, label, error, children }: FieldWrapperProps) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}
